import React from 'react';
import { Api, getToken, setToken } from '@utils';

interface User {
  id?: number;
  name?: string;
  email?: string;
  verified?: boolean;
  country?: string;
  created_at?: string;
  updated_at?: string;
  redirect?: string;
  completed?: boolean;
  token?: string;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  user: User;
  setUser: (user: User) => void;
  obData: any;
  setObData: (obData: any) => void;
  isVerified: boolean;
  setIsVerified: (isVerified: boolean) => void;
  logOut: () => void;
}

// Auth context
export const AuthContext = React.createContext<AuthContextProps>({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  isInitialized: false,
  setIsInitialized: () => {},
  user: {},
  setUser: () => {},
  obData: {},
  setObData: () => {},
  isVerified: false,
  setIsVerified: () => {},
  logOut: () => {},
});

export const useAuthContext = () => React.useContext(AuthContext);

// Auth Provider
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(true);
  const [user, setUser] = React.useState<User>({});
  const [obData, setObData] = React.useState({});
  const [isVerified, setIsVerified] = React.useState(false);

  React.useEffect(() => {
    const token = getToken();
    if (token) {
      setIsAuthenticated(true);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    const _getUser = async () => {
      setIsInitialized(false);

      const token = getToken();
      if (token) {
        try {
          const { data } = await Api.get('/profile', {
            headers: { Authorization: `Bearer ${token}` },
          });
          setObData(data.data.onboarding);
          setUser(data.data.user);
          setIsInitialized(true);
        } catch (error) {
          setIsAuthenticated(false);
          setUser({});
          setIsInitialized(true);
          setToken('');
        }
      }
    };

    if (isAuthenticated) _getUser();
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (user) {
      setIsVerified(user?.verified ?? false);
    }
  }, [user]);

  // Handle logout
  const logOut = async () => {
    setToken('');
    setIsAuthenticated(false);
    setUser({});
  };

  const memoizedValue = React.useMemo(
    () => ({
      isAuthenticated,
      setIsAuthenticated,
      isInitialized,
      setIsInitialized,
      user,
      setUser,
      obData,
      setObData,
      isVerified,
      setIsVerified,
      logOut,
    }),
    [isAuthenticated, isVerified, isInitialized, logOut, obData, user]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};
